import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send, Mail } from 'lucide-react';
import { Button } from './Button';
import { TokenPicker, TokenDef } from './TokenPicker';

const DEFAULT_TOKENS: TokenDef[] = [
    { token: '{{prenom}}', desc: 'Prénom du destinataire' },
    { token: '{{nom}}', desc: 'Nom (ou raison sociale) du destinataire' },
    { token: '{{entreprise}}', desc: 'Votre établissement' },
    { token: '{{logo}}', desc: 'Votre logo' },
];

/** Modale générique "message personnalisé" — réutilisée depuis la fiche contact CRM, la bulle d'un
 *  rendez-vous Prestation (salarié et client/admin) et la bulle d'un événement Événementiel. Le seul point
 *  variable est la fonction d'envoi passée en prop et la liste de jetons disponibles (certains contextes,
 *  comme un rendez-vous, ont des jetons en plus comme {{date}}/{{heure}}). */
export function SendCustomMessageModal({
    recipientLabel,
    tokens = DEFAULT_TOKENS,
    onClose,
    onSend,
}: {
    recipientLabel: string;
    tokens?: TokenDef[];
    onClose: () => void;
    onSend: (subject: string, body: string) => Promise<{ success?: boolean; error?: string }>;
}) {
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [sent, setSent] = useState(false);
    const bodyRef = useRef<HTMLTextAreaElement>(null);

    const handleSend = async () => {
        if (!subject.trim() || !body.trim()) return;
        setSending(true);
        setError('');
        try {
            const res = await onSend(subject.trim(), body.trim());
            if (res?.error) { setError(res.error); return; }
            setSent(true);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'envoi.');
        } finally {
            setSending(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    className="bg-surface border border-border rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
                >
                    <div className="flex items-center justify-between p-5 border-b border-border">
                        <h2 className="text-base font-bold text-content flex items-center gap-2"><Mail size={17} className="text-brand-500" /> Message à {recipientLabel}</h2>
                        <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft transition-all"><X size={18} /></button>
                    </div>

                    {sent ? (
                        <div className="p-6 space-y-3 text-center">
                            <p className="text-sm text-content">Message envoyé.</p>
                            <Button size="md" variant="primary" onClick={onClose}>Fermer</Button>
                        </div>
                    ) : (
                        <>
                            <div className="p-5 space-y-3">
                                <div>
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Sujet</label>
                                    <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Message</label>
                                    <textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={6}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors resize-none" />
                                </div>
                                <TokenPicker tokens={tokens} textareaRef={bodyRef} value={body} onChange={setBody} />
                                {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                            </div>
                            <div className="flex justify-end gap-3 p-5 border-t border-border">
                                <Button size="md" variant="secondary" onClick={onClose}>Annuler</Button>
                                <Button size="md" variant="primary" onClick={handleSend} disabled={sending || !subject.trim() || !body.trim()}>
                                    <Send size={15} /> {sending ? 'Envoi...' : 'Envoyer'}
                                </Button>
                            </div>
                        </>
                    )}
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
